import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FaShoppingCart, FaCog, FaBoxOpen, FaStore, FaTruck,
  FaUndo, FaExclamationTriangle, FaTimes, FaCheckCircle, FaCheck,
} from 'react-icons/fa';
import { FaBox } from 'react-icons/fa';

const Trackorder = () => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { orderId: paramOrderId } = useParams();
  const [searchParams] = useSearchParams();

  const getCustomerId = () => {
    try {
      const directId = localStorage.getItem('customer_id');
      if (directId) return directId;
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.customer_id || null;
      }
    } catch (e) { console.error('Error getting customer ID:', e); }
    return null;
  };

  const fetchTrackingData = async (id) => {
    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();

      if (!token || !customerId) {
        setError('Please login to track your order.');
        setLoading(false);
        return;
      }

      const detailsUrl = `https://evercorenutrition.com/ev_admin/api/orders/${id}/?customer_id=${customerId}`;
      const detailsResponse = await fetch(detailsUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (detailsResponse.ok) {
        const data = await detailsResponse.json();
        const transformedData = {
          success: true,
          order: {
            order_id: data.order_id,
            status: data.status,
            status_display: data.status_display,
            created_at: data.created_at,
            total_amount: parseFloat(data.total_amount),
            payment_type: data.payment_type,
          },
          timeline: data.statuses || [],
          fulfillment: data.fulfillment,
          estimated_delivery: data.schedule?.estimated_delivery || null,
        };
        setOrderData(transformedData);
        setLoading(false);
        return;
      }

      const trackResponse = await fetch(
        `https://evercorenutrition.com/ev_admin/api/orders/${id}/track/?customer_id=${customerId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (trackResponse.ok) {
        const data = await trackResponse.json();
        if (data.success) setOrderData(data);
        else setError('Could not retrieve tracking information.');
      } else {
        if (trackResponse.status === 500) {
          setError('Tracking service temporarily unavailable. Please try again later.');
        } else {
          const err = await trackResponse.json().catch(() => ({}));
          setError(err.message || err.error || 'Order not found. Please check the Order ID.');
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = paramOrderId || searchParams.get('order_id');
    if (id) {
      setSearchInput(id);
      setOrderId(id);
      fetchTrackingData(id);
    }
  }, [paramOrderId, searchParams]);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) { setError('Please enter an Order ID.'); return; }
    setOrderId(trimmed);
    fetchTrackingData(trimmed);
  };

  // ── Updated status pipeline ──
  const ALL_STEPS = [
    { key: 'new order',        label: 'New Order',        icon: <FaShoppingCart /> },
    { key: 'processing',       label: 'Processing',       icon: <FaCog /> },
    { key: 'ready to ship',    label: 'Ready to Ship',    icon: <FaBoxOpen /> },
    { key: 'in transit',       label: 'In Transit',       icon: <FaTruck /> },
    { key: 'return',           label: 'Return',           icon: <FaUndo /> },
    { key: 'cancel requested', label: 'Cancel Requested', icon: <FaExclamationTriangle /> },
    { key: 'cancelled',        label: 'Cancelled',        icon: <FaTimes /> },
    { key: 'completed',        label: 'Completed',        icon: <FaCheckCircle /> },
  ];

  // Updated branch definitions
  const CANCEL_BRANCH   = ['new order', 'processing', 'cancel requested', 'cancelled'];
  const RETURN_BRANCH   = ['new order', 'processing', 'ready to ship', 'in transit', 'return'];
  const DEFAULT_BRANCH  = ['new order', 'processing', 'ready to ship', 'in transit', 'completed'];

  // Map API status to step keys - FIXED for "In Transist" spelling
  const mapStatusToStepKey = (status) => {
    if (!status) return '';
    
    const s = status.toLowerCase().trim();
    
    // Direct matches
    if (s === 'new order' || s === 'pending' || s === 'order placed') return 'new order';
    if (s === 'processing' || s === 'in process' || s === 'order processing') return 'processing';
    if (s === 'ready to ship' || s === 'ready_to_ship' || s === 'packed') return 'ready to ship';
    
    // Handle "In Transist" (with 's') and other variations
    if (s === 'in transist' || s === 'in_transist' || s === 'in transit' || s === 'in_transit' || 
        s === 'shipped' || s === 'out for delivery' || s === 'drop') return 'in transit';
    
    if (s === 'return' || s === 'returned' || s === 'return request') return 'return';
    if (s === 'cancel requested' || s === 'cancel_requested' || s === 'cancellation requested') return 'cancel requested';
    if (s === 'cancelled' || s === 'canceled') return 'cancelled';
    if (s === 'completed' || s === 'delivered' || s === 'complete') return 'completed';
    
    // Default fallback
    return s;
  };

  const getActiveBranch = (status) => {
    const stepKey = mapStatusToStepKey(status);
    if (['cancelled', 'cancel requested'].includes(stepKey)) return CANCEL_BRANCH;
    if (stepKey === 'return') return RETURN_BRANCH;
    return DEFAULT_BRANCH;
  };

  const getStatusMeta = (status) => {
    const stepKey = mapStatusToStepKey(status);
    // Cancelled/Return get a red tint; everything else is black
    if (['cancelled', 'cancel requested'].includes(stepKey)) return { color: '#ef4444', bg: '#fee2e2' };
    if (stepKey === 'return') return { color: '#ef4444', bg: '#fee2e2' };
    if (stepKey === 'completed') return { color: '#111111', bg: '#f0f0f0' };
    return { color: '#111111', bg: '#f0f0f0' };
  };

  const getCurrentIndex = (status, branch) => {
    const stepKey = mapStatusToStepKey(status);
    const idx = branch.indexOf(stepKey);
    return idx;
  };

  const order       = orderData?.order;
  const timeline    = orderData?.timeline || [];
  const fulfillment = orderData?.fulfillment;
  const estimatedDelivery = orderData?.estimated_delivery;

  // Get the mapped step key for display
  const mappedStepKey = order ? mapStatusToStepKey(order.status_display) : '';
  const activeBranch   = order ? getActiveBranch(order.status_display) : DEFAULT_BRANCH;
  const currentIdx     = order ? getCurrentIndex(order.status_display, activeBranch) : -1;
  const statusMeta     = order ? getStatusMeta(order.status_display) : {};
  const stepInfoMap    = Object.fromEntries(ALL_STEPS.map(s => [s.key, s]));

  // Debug - helps identify what's coming from API
  // console.log('Order Status:', order?.status_display);
  // console.log('Mapped Step Key:', mappedStepKey);
  // console.log('Active Branch:', activeBranch);
  // console.log('Current Index:', currentIdx);

  return (
    <>
      <Navbar />

      {/* Breadcrumb */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Order Tracking</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/"><i className="fa-solid fa-house"></i></a></li>
                    <li className="breadcrumb-item active">Order Tracking</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="track-search-section">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="track-search-container" style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
                marginBottom: '30px'
              }}>
                <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>Track Your Order</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Enter Order ID (e.g., ORD-HSN400BF)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '250px',
                      padding: '12px 15px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    style={{
                      padding: '12px 25px',
                      background: '#000',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Tracking...' : 'Track Order'}
                  </button>
                </div>
                {error && (
                  <div style={{
                    marginTop: '15px',
                    padding: '10px 15px',
                    background: '#fee2e2',
                    color: '#ef4444',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}>
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Order Detail Section */}
      {order && (
        <section className="order-detail">
          <div className="container-fluid-lg">
            <div className="row g-sm-4 g-3">

              {/* Left — Order Info Card */}
              <div className="col-xxl-3 col-xl-4 col-lg-6">
                <div style={{ background: '#fff', borderRadius: '16px', padding: '25px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ fontSize: '60px' }}>
                      <FaBox />
                    </div>
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      background: statusMeta.bg, 
                      color: statusMeta.color, 
                      padding: '5px 16px', 
                      borderRadius: '20px', 
                      fontWeight: '700', 
                      fontSize: '13px', 
                      marginTop: '10px', 
                      border: `1.5px solid ${statusMeta.color}33` 
                    }}>
                      {order.status_display}
                    </span>
                    {/* Show mapped status for debugging - remove after testing */}
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '5px' }}>
                      Mapped: {mappedStepKey || 'Not mapped'}
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    {[
                      { label: 'Order ID', value: order.order_id },
                      { label: 'Amount',   value: `₹${parseFloat(order.total_amount).toFixed(2)}` },
                      { label: 'Payment',  value: order.payment_type },
                      { label: 'Ordered On', value: new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ color: '#888', fontSize: '13px' }}>{label}</span>
                        <span style={{ fontWeight: '700', fontSize: '13px' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — Tracking Details */}
              <div className="col-xxl-9 col-xl-8 col-lg-6">
                <div className="row g-sm-4 g-3">

                  {/* Info tiles */}
                  {[
                    { icon: 'package',   label: 'Order ID',          value: order.order_id },
                    { icon: 'truck',     label: 'Shipping Provider',  value: fulfillment?.provider || fulfillment?.shipping_provider || 'Not assigned yet' },
                    { icon: 'info',      label: 'Payment',            value: order.payment_type },
                    { icon: 'crosshair', label: 'Order Placed',       value: new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
                    { icon: 'map-pin',   label: 'Tracking Code',      value: fulfillment?.awb_number || fulfillment?.tracking_number || 'N/A' },
                    { icon: 'calendar',  label: 'Estimated Delivery',  value: estimatedDelivery ? new Date(estimatedDelivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not available' },
                  ].map(({ icon, label, value }) => (
                    <div className="col-xl-4 col-sm-6" key={label}>
                      <div className="order-details-contain">
                        <div className="order-tracking-icon">
                          <i data-feather={icon} className="text-content"></i>
                        </div>
                        <div className="order-details-name">
                          <h5 className="text-content">{label}</h5>
                          <h4 className="theme-color" style={{ fontSize: '15px' }}>{value}</h4>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* ── Progress Tracker ── */}
                  <div className="col-12">
                    <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: `${activeBranch.length * 130}px`, position: 'relative', padding: '24px 0 8px' }}>

                        {/* Connecting line */}
                        <div style={{ position: 'absolute', top: '36px', left: '40px', right: '40px', height: '3px', background: '#e5e7eb', borderRadius: '2px', zIndex: 0 }} />
                        {/* Filled portion */}
                        <div style={{
                          position: 'absolute', top: '36px', left: '40px',
                          width: currentIdx >= 0 ? `${(currentIdx / (activeBranch.length - 1)) * 100}%` : '0%',
                          height: '3px', background: '#111',
                          borderRadius: '2px', zIndex: 1,
                          transition: 'width 0.6s ease',
                        }} />

                        {activeBranch.map((stepKey, idx) => {
                          const info = stepInfoMap[stepKey] || { label: stepKey, icon: <FaCog /> };
                          const done    = idx < currentIdx;
                          const active  = idx === currentIdx;
                          const pending = idx > currentIdx;

                          return (
                            <div key={stepKey} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2 }}>
                              {/* Circle */}
                              <div style={{
                                width: '48px', height: '48px', borderRadius: '50%',
                                background: done ? '#111' : active ? '#111' : '#fff',
                                border: `3px solid ${done || active ? '#111' : '#d1d5db'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: active ? '0 0 0 5px rgba(0,0,0,0.12)' : 'none',
                                transition: 'all 0.3s ease',
                                marginBottom: '10px',
                              }}>
                                {done
                                  ? <FaCheck size={14} color="#fff" />
                                  : <span style={{ color: active ? '#fff' : pending ? '#9ca3af' : '#111', display: 'flex' }}>
                                      {React.cloneElement(info.icon, { size: 16 })}
                                    </span>
                                }
                              </div>

                              {/* Label */}
                              <p style={{
                                margin: 0, fontSize: '11px', fontWeight: active ? '700' : '500',
                                color: active ? '#111' : done ? '#374151' : '#9ca3af',
                                textAlign: 'center', lineHeight: '1.3', maxWidth: '90px',
                              }}>
                                {info.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Timeline Table */}
      <section className="order-table-section section-b-space">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table order-tab-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order && timeline.length === 0 ? (
                      <>
                        <tr>
                          <td>Order Placed</td>
                          <td>{new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td>{new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                          <td>Bhabua, India</td>
                        </tr>
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '20px', fontStyle: 'italic' }}>
                            No further tracking updates yet. Check back soon.
                          </td>
                        </tr>
                      </>
                    ) : timeline.length > 0 ? (
                      timeline.map((entry, idx) => (
                        <tr key={idx}>
                          <td>{entry.status_display || entry.status}</td>
                          <td>{new Date(entry.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td>{new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                          <td>{entry.location || 'Surat, India'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
                          Enter an Order ID above to track your order.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Trackorder;